import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useEffect, type ReactNode } from 'react';
import Reanimated, {
  interpolateColor,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withSequence,
  withTiming,
} from 'react-native-reanimated';

import { Heading } from '@/components/heading';
import { Divider } from '@/components/ui/divider';
import { HStack } from '@/components/ui/hstack';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { useColors } from '@/hooks/useColors';

/**
 * The text of a conversation entry's body. Pass it to anything that draws the body's words, such
 * as the answers markdown, so a reply reads at the same size and color as a comment.
 */
export const CONVERSATION_BODY_TEXT_CLASS =
  'text-left font-normal text-sm leading-6 text-typography-800 web:text-base';

/**
 * A plain-text body, with an optional "@name" lead-in for a reply to someone. The lead-in sits
 * inside the same text run, so it wraps with the words that follow it.
 */
export function ConversationText({
  mention,
  children,
}: {
  mention?: string | null;
  children: ReactNode;
}) {
  return (
    <Text className={CONVERSATION_BODY_TEXT_CLASS}>
      {mention && (
        <Text className="font-medium text-sm text-typography-900 web:text-base">@{mention} </Text>
      )}
      {children}
    </Text>
  );
}

type ConversationEntryProps = {
  avatar: ReactNode;
  name: string;
  /** Drawn after a dot beside the name. Nothing, dot included, when it is empty. */
  timeLabel?: string | null;
  /** A line under the name, such as a comment author's role and state. */
  meta?: ReactNode;
  body: ReactNode;
  footer?: ReactNode;
  /**
   * A nested reply's header row, which a comment reply uses: one more wrapper and a wider gap
   * between the avatar and the name.
   */
  nested?: boolean;
  /**
   * The name's heading level. Comments sit under the post as h3. Inside an answer card, whose
   * header is h4, a reply's name is h5 so the outline stays in order. The size is the same.
   */
  level?: 3 | 5;
  /** Flashes the line and the body once, for the comment a link points at. */
  highlighted?: boolean;
  /**
   * The vertical line beside the body. Comments keep it. A follow-up inside an answer card drops
   * it, since the card already groups the replies, and its body starts under the name instead.
   */
  line?: boolean;
};

/**
 * One entry of a conversation, the layout a comment draws and an answer's follow-ups reuse: a
 * header row with the avatar, the name as a heading, a dot and the time, then the body beside a
 * thin vertical line that runs down from under the avatar, then an optional footer.
 *
 * Presentational only. Each caller decides what goes in the slots: comments pass their avatar,
 * a relative time, the "@name" lead-in and Like and Reply; the answers pass the specialist avatar,
 * an exact time and no footer. The classes are the comment's own, moved here unchanged, and the
 * comment snapshots hold them there, because comments draw this with the answers flag off too.
 *
 * It renders a header and a body block as siblings, so the caller's wrapper owns the entry's id,
 * its indent and anything nested under it.
 */
export function ConversationEntry({
  avatar,
  name,
  timeLabel,
  meta,
  body,
  footer,
  nested = false,
  level = 3,
  highlighted = false,
  line = true,
}: ConversationEntryProps) {
  if (!line) {
    return (
      <>
        <ConversationHeader
          avatar={avatar}
          name={name}
          timeLabel={timeLabel}
          meta={meta}
          nested={nested}
          level={level}
          alignStart
        />
        {/* 32px avatar plus the header's 12px gap, so the text lines up with the name. */}
        <VStack className="ml-11 mt-1">{body}</VStack>
        {footer}
      </>
    );
  }

  return (
    <>
      <ConversationHeader
        avatar={avatar}
        name={name}
        timeLabel={timeLabel}
        meta={meta}
        nested={nested}
        level={level}
      />
      <VStack className={isWeb ? 'ml-4' : ''}>
        <ConversationBody highlighted={highlighted}>{body}</ConversationBody>
        {footer}
      </VStack>
    </>
  );
}

function ConversationHeader({
  avatar,
  name,
  timeLabel,
  meta,
  nested,
  level,
  alignStart = false,
}: Required<Pick<ConversationEntryProps, 'nested' | 'level'>> &
  Pick<ConversationEntryProps, 'avatar' | 'name' | 'timeLabel' | 'meta'> & {
    /**
     * Starts the name at the left edge of its column. A comment centers it, which only shows
     * when the line under the name is the wider one, as a follow-up's date is.
     */
    alignStart?: boolean;
  }) {
  const content = (
    <>
      {avatar}
      <VStack>
        <HStack
          className={
            alignStart
              ? 'items-center gap-1 text-typography-800'
              : 'items-center justify-center gap-1 text-typography-800'
          }>
          <Heading
            size={level === 5 ? 'h5-sm' : 'h3-sm'}
            className="font-medium text-typography-900">
            {name}
          </Heading>
          {timeLabel && (
            <>
              <Text className="text-xs">•</Text>
              <Text className="text-xs text-typography-700">{timeLabel}</Text>
            </>
          )}
        </HStack>
        {meta}
      </VStack>
    </>
  );

  if (nested) {
    return (
      <HStack className="items-center">
        <HStack className="items-center justify-center gap-4">{content}</HStack>
      </HStack>
    );
  }

  return <VStack className="flex-row items-center gap-3">{content}</VStack>;
}

/**
 * The body beside its vertical line. When `highlighted` turns on, the line takes the brand color
 * and the body a pale info fill, then both fade back, sooner on web where the page does not move.
 */
function ConversationBody({
  highlighted,
  children,
}: {
  highlighted: boolean;
  children: ReactNode;
}) {
  const progress = useSharedValue(0);

  const { colors, isDarkScheme } = useColors();

  useEffect(() => {
    if (!highlighted) {
      return;
    }

    progress.value = withSequence(
      withTiming(1, { duration: 300 }),
      withDelay(isWeb ? 3000 : 7000, withTiming(0, { duration: 1000 }))
    );
  }, [highlighted, progress]);

  const animatedDividerStyle = useAnimatedStyle(() => {
    return {
      backgroundColor: interpolateColor(
        progress.value,
        [0, 1],
        [colors.outline['100'], colors.secondary['500']]
      ),
      flex: 1,
    };
  });

  const animatedBgStyle = useAnimatedStyle(() => {
    const startColor = isDarkScheme ? 'rgba(0,0,0,0)' : 'rgba(255, 255, 255, 0)';

    return {
      backgroundColor: interpolateColor(progress.value, [0, 1], [startColor, colors.info['0']]),
      paddingHorizontal: 12,
      paddingVertical: 8,
    };
  });

  return (
    <HStack>
      <Divider className="my-3 ml-4 mr-5 w-0.5 web:ml-0" orientation="vertical">
        <Reanimated.View style={animatedDividerStyle} />
      </Divider>
      <VStack className="border-ra mb-1 mt-3">
        <Reanimated.View style={animatedBgStyle}>{children}</Reanimated.View>
      </VStack>
    </HStack>
  );
}
